import React, { useCallback, useEffect, useState } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import DownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { RoleItem } from '../../../common/types';
import { ANALYTICS_FILTER_DROPDOWN, ANALYTICS_FILTER_DROPDOWN_ITEM } from './constants';
import { filterByRolesText } from './messages';
import type { Props } from './types';
import { getRolesDropdownLabel } from './utils';

export const RolesFilter = (props: Props) => {
	const { roles: initRoles, intl, onChange, isOpenByDefault = false } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [roles, _setRoles] = useState<RoleItem[]>(initRoles);
	useEffect(() => {
		_setRoles(initRoles);
	}, [initRoles]);

	const toggleRole = useCallback((role: RoleItem) => {
		_setRoles((oldRoles) =>
			oldRoles.map((r) => (r.id === role.id ? { ...r, isChecked: !r.isChecked } : r)),
		);
	}, []);

	const onItemToggled = useCallback(
		(item: RoleItem): void => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'roleOption',
				attributes: {
					isSPA: false,
					namespaces: '',
					source: 'filterRoleDropdown',
				},
			});

			fireUIAnalytics(analyticsEvent, ANALYTICS_FILTER_DROPDOWN_ITEM, {
				source: ANALYTICS_FILTER_DROPDOWN,
			});
			onChange(item.id, !item.isChecked);
			toggleRole(item);
		},
		[createAnalyticsEvent, onChange, toggleRole],
	);

	const onDropdownClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'filterRoleDropdown',
			attributes: {
				isSPA: false,
				namespaces: '',
			},
		});

		fireUIAnalytics(analyticsEvent, ANALYTICS_FILTER_DROPDOWN);
	}, [createAnalyticsEvent]);

	return (
		<div data-testid="users-and-roles.ui.roles-filter-container.roles-filter.dropdown">
			<DropdownMenu
				onOpenChange={onDropdownClick}
				trigger={({ triggerRef, ...propsTrigger }: CustomTriggerProps) => (
					<Button
						{...propsTrigger}
						aria-label={intl.formatMessage(filterByRolesText.buttonLabel)}
						iconAfter={<DownIcon label={intl.formatMessage(filterByRolesText.openLabel)} />}
						ref={triggerRef}
					>
						{getRolesDropdownLabel(roles, intl)}
					</Button>
				)}
				defaultOpen={isOpenByDefault}
			>
				<DropdownItemCheckboxGroup
					id="users-and-roles.ui.roles-filter-container.roles-filter.checkbox-group"
					testId="users-and-roles.ui.roles-filter-container.roles-filter.checkbox-group"
				>
					{roles.map((role) => (
						<DropdownItemCheckbox
							isSelected={role.isChecked}
							id={role.id.toString()}
							key={role.id}
							onClick={() => onItemToggled(role)}
						>
							{role.translatedName}
						</DropdownItemCheckbox>
					))}
				</DropdownItemCheckboxGroup>
			</DropdownMenu>
		</div>
	);
};
RolesFilter.displayName = 'RolesFilter';

export default injectIntl(RolesFilter);
